import { useState, useEffect } from 'react'
import { Header } from '@tanstack/react-table'

interface SortConfig {
  key: string
  direction: 'asc' | 'desc'
}

interface UseListViewSortingProps {
  defaultSortBy: string
  defaultDirection?: 'asc' | 'desc'
  onSortChange?: (sort: string, sortBy: string) => Promise<void>
  setIsLoading?: (loading: boolean) => void
}

export const useListViewSorting = ({
  defaultSortBy,
  defaultDirection = 'desc',
  onSortChange,
  setIsLoading,
}: UseListViewSortingProps) => {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: defaultSortBy,
    direction: defaultDirection,
  })

  // Reset to default if sortConfig.key is empty
  useEffect(() => {
    if (sortConfig.key === '') {
      setSortConfig({ key: defaultSortBy, direction: defaultDirection })
    }
  }, [sortConfig.key, defaultSortBy, defaultDirection])

  const createSortHandler = <TData,>(property: Header<TData, unknown>) => async () => {
    if (setIsLoading) setIsLoading(true)

    // Check if the current sort key is the same as the clicked column and if the direction is 'asc'.
    const isAsc =
      sortConfig.key === property.id && sortConfig.direction === 'asc'

    let propText = property?.id || ''
    // If the column header is not 'Dags. skráð', append '.sort' to the property text.
    // TODO: This only works for the first iteration of most of the columns.
    const header = property.column.columnDef.header
    if (typeof header === 'string' && header !== 'Dags. skráð') {
      propText += '.sort'
    }

    // Create a new sort configuration. If the current direction is 'asc', change it to 'desc', and vice versa.
    const newSortConfig: SortConfig = {
      key: property.id,
      direction: isAsc ? ('desc' as const) : ('asc' as const),
    }
    setSortConfig(newSortConfig)

    // Create an object with the new sort direction and sort key to be used for refetching data.
    if (onSortChange) {
      await onSortChange(newSortConfig.direction, propText)
    }
    if (setIsLoading) setIsLoading(false)
  }

  return {
    sortConfig,
    setSortConfig,
    createSortHandler,
  }
}
